import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import styled from "styled-components";
import { FiArrowLeft, FiPlus, FiEdit2, FiCheck, FiX } from "react-icons/fi";
import { fetchBays, addBay, updateBays } from "../utils/api";
import {
  PageHeader,
  PageTitle,
  PageSubtitle,
  Button,
  GhostButton,
  IconButton,
  Card,
  Input,
  Select,
  Badge,
  DayChip,
  EmptyState,
  Spinner,
  Row,
  FieldError,
  Label,
} from "../components/ui";
import { formatDate } from "../utils/format";
import ErrorNotification from "../components/ErrorNotification";

const dayMapToFull = { M: "Monday", Tu: "Tuesday", W: "Wednesday", Th: "Thursday", F: "Friday", Sa: "Saturday", Su: "Sunday" };
const dayMapToShort = { Monday: "M", Tuesday: "Tu", Wednesday: "W", Thursday: "Th", Friday: "F", Saturday: "Sa", Sunday: "Su" };
const ALL_DAYS = ["M", "Tu", "W", "Th", "F", "Sa", "Su"];

const TableWrap = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 12px 14px;
    text-align: left;
    vertical-align: middle;
  }

  thead th {
    background: ${({ theme }) => theme.colors.bgAlt};
    color: ${({ theme }) => theme.colors.textMuted};
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tbody tr { border-bottom: 1px solid ${({ theme }) => theme.colors.border}; }
  tbody tr:last-child { border-bottom: 0; }
  tbody tr:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }

  ${({ theme }) => theme.media.lg} {
    display: none;
  }
`;

const CardList = styled.div`
  display: none;
  ${({ theme }) => theme.media.lg} {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space(3)};
    padding: ${({ theme }) => theme.space(3)};
  }
`;

const BayCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space(4)};

  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 12px; }
  .id { font-weight: 600; font-size: 15px; }
  .floor { font-size: 12px; color: ${({ theme }) => theme.colors.textMuted}; }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    font-size: 13px;
    margin-bottom: 12px;
  }
  .k { color: ${({ theme }) => theme.colors.textMuted}; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 2px; }
  .days { display: flex; flex-wrap: wrap; }
`;

const FormCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.space(4)};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.space(4)};
  margin-bottom: ${({ theme }) => theme.space(4)};
`;

const DaysRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const SiteDetails = () => {
  const { siteId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  const { register, handleSubmit, setValue, reset, control, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      bay_identifier: "",
      floor: "",
      selected_days: ALL_DAYS,
      start_datetime: "",
      end_datetime: "",
      offering: "CASUAL",
      status: "ACTIVE",
      vip: "0",
      instruction: "",
    },
    mode: "onChange",
  });

  const [bays, setBays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("view"); // 'view' | 'add' | 'edit'
  const [editingBayId, setEditingBayId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (!siteId) throw new Error("siteId is missing");
        const data = await fetchBays(auth.user?.id_token, siteId);
        if (!cancelled) setBays(data);
      } catch (err) {
        if (!cancelled) setErrorMessage("Failed to load bays: " + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [auth.user?.id_token, siteId]);

  const beginAdd = () => {
    reset({
      bay_identifier: "",
      floor: "",
      selected_days: ALL_DAYS,
      start_datetime: "",
      end_datetime: "",
      offering: "CASUAL",
      status: "ACTIVE",
      vip: "0",
      instruction: "",
    });
    setEditingBayId(null);
    setMode("add");
  };

  const beginEdit = (bay) => {
    const shortDays = (bay.selected_days || []).map((d) => dayMapToShort[d]).filter(Boolean);
    setValue("bay_identifier", bay.bay_identifier);
    setValue("floor", bay.floor);
    setValue("selected_days", shortDays);
    setValue("start_datetime", bay.start_datetime?.slice(0, 10) || "");
    setValue("end_datetime", bay.end_datetime?.slice(0, 10) || "");
    setValue("offering", bay.offering);
    setValue("status", bay.status);
    setValue("vip", String(bay.vip ?? 0));
    setValue("instruction", bay.instruction || "");
    setEditingBayId(bay.bay_id);
    setMode("edit");
  };

  const cancelForm = () => {
    setMode("view");
    setEditingBayId(null);
    reset();
  };

  const submitForm = async (data) => {
    try {
      const fullDays = (data.selected_days || []).map((d) => dayMapToFull[d]).filter(Boolean);
      const payload = {
        site_id: siteId,
        bayDetails: [{
          ...(mode === "edit" && editingBayId ? { bay_id: editingBayId } : {}),
          bay_identifier: data.bay_identifier,
          floor: data.floor,
          selected_days: fullDays,
          start_datetime: data.start_datetime,
          end_datetime: data.end_datetime,
          offering: data.offering,
          status: data.status,
          vip: parseInt(data.vip, 10),
          instruction: data.instruction,
        }],
      };
      if (mode === "edit") {
        await updateBays(auth.user?.id_token, payload);
      } else {
        await addBay(auth.user?.id_token, payload);
      }
      const updated = await fetchBays(auth.user?.id_token, siteId);
      setBays(updated);
      cancelForm();
    } catch (err) {
      setErrorMessage(`Failed to ${mode === "edit" ? "update" : "add"} bay: ${err.message}`);
    }
  };

  const siteName = state?.siteName || "Site";

  const renderBayForm = () => (
    <FormCard>
      <form onSubmit={handleSubmit(submitForm)}>
        <FormGrid>
          <div>
            <Label>Bay ID</Label>
            <Input {...register("bay_identifier", { required: "Bay ID is required" })} error={!!errors.bay_identifier} />
            {errors.bay_identifier && <FieldError>{errors.bay_identifier.message}</FieldError>}
          </div>
          <div>
            <Label>Floor</Label>
            <Input {...register("floor", { required: "Floor is required" })} error={!!errors.floor} />
            {errors.floor && <FieldError>{errors.floor.message}</FieldError>}
          </div>
          <div>
            <Label>Start date</Label>
            <Input type="date" {...register("start_datetime", { required: "Start date is required" })} error={!!errors.start_datetime} />
            {errors.start_datetime && <FieldError>{errors.start_datetime.message}</FieldError>}
          </div>
          <div>
            <Label>End date</Label>
            <Input type="date" {...register("end_datetime", { required: "End date is required" })} error={!!errors.end_datetime} />
            {errors.end_datetime && <FieldError>{errors.end_datetime.message}</FieldError>}
          </div>
          <div>
            <Label>Offering</Label>
            <Select {...register("offering")}>
              <option value="CASUAL">Casual</option>
              <option value="LONG_TERM">Long term</option>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select {...register("status")}>
              <option value="ACTIVE">Active</option>
              <option value="HOLD">Hold</option>
            </Select>
          </div>
          <div>
            <Label>VIP</Label>
            <Select {...register("vip")}>
              <option value="0">Regular</option>
              <option value="1">VIP</option>
            </Select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <Label>Instruction</Label>
            <Input {...register("instruction")} placeholder="Special instructions for this bay…" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <Label>Available days</Label>
            <Controller
              control={control}
              name="selected_days"
              render={({ field }) => (
                <DaysRow>
                  {ALL_DAYS.map((day) => {
                    const selected = field.value?.includes(day);
                    return (
                      <DayChip
                        key={day}
                        type="button"
                        selected={selected}
                        onClick={() =>
                          field.onChange(
                            selected ? field.value.filter((d) => d !== day) : [...(field.value || []), day]
                          )
                        }
                      >
                        {day}
                      </DayChip>
                    );
                  })}
                </DaysRow>
              )}
            />
          </div>
        </FormGrid>
        <Row justify="flex-end" gap={2}>
          <GhostButton type="button" onClick={cancelForm}><FiX /> Cancel</GhostButton>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? <Spinner size={14} /> : <FiCheck />}
            {mode === "edit" ? "Save changes" : "Add bay"}
          </Button>
        </Row>
      </form>
    </FormCard>
  );

  return (
    <>
      <Row gap={2} style={{ marginBottom: 16 }}>
        <GhostButton onClick={() => navigate("/sites")}>
          <FiArrowLeft /> Back to sites
        </GhostButton>
      </Row>

      <PageHeader>
        <div>
          <PageTitle>{siteName}</PageTitle>
          <PageSubtitle>{loading ? "Loading bays…" : `${bays.length} bay${bays.length === 1 ? "" : "s"} configured`}</PageSubtitle>
        </div>
        {mode === "view" && (
          <Button onClick={beginAdd}>
            <FiPlus /> Add bay
          </Button>
        )}
      </PageHeader>

      {mode !== "view" && renderBayForm()}

      <TableWrap>
        {loading ? (
          <div style={{ padding: 24, display: "flex", justifyContent: "center" }}><Spinner /></div>
        ) : bays.length === 0 && mode === "view" ? (
          <EmptyState>
            <h3>No bays yet</h3>
            <p>Add the first bay to begin accepting bookings for this site.</p>
            <Button onClick={beginAdd}><FiPlus /> Add bay</Button>
          </EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <th>Bay</th>
                  <th>Floor</th>
                  <th>Days</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Offering</th>
                  <th>Status</th>
                  <th>VIP</th>
                  <th>Instruction</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bays.map((bay) => (
                  <tr key={bay.bay_id}>
                    <td><strong>{bay.bay_identifier}</strong></td>
                    <td>{bay.floor}</td>
                    <td>
                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                        {ALL_DAYS.map((d) => (
                          <DayChip key={d} as="span" selected={bay.selected_days?.includes(dayMapToFull[d])}>
                            {d}
                          </DayChip>
                        ))}
                      </div>
                    </td>
                    <td>{formatDate(bay.start_datetime)}</td>
                    <td>{formatDate(bay.end_datetime)}</td>
                    <td><Badge tone="neutral">{bay.offering}</Badge></td>
                    <td><Badge tone={bay.status === "ACTIVE" ? "success" : "warning"}>{bay.status}</Badge></td>
                    <td>{bay.vip === 1 ? <Badge tone="accent">VIP</Badge> : <span style={{ color: "#8E8B86" }}>Regular</span>}</td>
                    <td style={{ maxWidth: 240, color: "#6B6864" }}>{bay.instruction || "—"}</td>
                    <td>
                      <Row justify="flex-end">
                        <IconButton onClick={() => beginEdit(bay)} aria-label="Edit bay" disabled={mode !== "view"}>
                          <FiEdit2 />
                        </IconButton>
                      </Row>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <CardList>
              {bays.map((bay) => (
                <BayCard key={bay.bay_id}>
                  <div className="head">
                    <div>
                      <div className="id">{bay.bay_identifier}</div>
                      <div className="floor">Floor {bay.floor}</div>
                    </div>
                    <Row gap={1}>
                      <Badge tone={bay.status === "ACTIVE" ? "success" : "warning"}>{bay.status}</Badge>
                      {bay.vip === 1 && <Badge tone="accent">VIP</Badge>}
                    </Row>
                  </div>
                  <div className="grid">
                    <div>
                      <div className="k">Offering</div>
                      {bay.offering}
                    </div>
                    <div>
                      <div className="k">Window</div>
                      {formatDate(bay.start_datetime)} → {formatDate(bay.end_datetime)}
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div className="k">Available days</div>
                      <div className="days">
                        {ALL_DAYS.map((d) => (
                          <DayChip key={d} as="span" selected={bay.selected_days?.includes(dayMapToFull[d])}>
                            {d}
                          </DayChip>
                        ))}
                      </div>
                    </div>
                    {bay.instruction && (
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div className="k">Instruction</div>
                        {bay.instruction}
                      </div>
                    )}
                  </div>
                  <Row justify="flex-end">
                    <IconButton onClick={() => beginEdit(bay)} aria-label="Edit bay" disabled={mode !== "view"}>
                      <FiEdit2 />
                    </IconButton>
                  </Row>
                </BayCard>
              ))}
            </CardList>
          </>
        )}
      </TableWrap>

      {errorMessage && (
        <ErrorNotification
          message={errorMessage}
          duration={4000}
          onClose={() => setErrorMessage(null)}
        />
      )}
    </>
  );
};

export default SiteDetails;
